import { useQueryClient } from "@tanstack/react-query";
import { useBuildingsResource } from "../../../buildings/hooks/useBuildingsResource";
import { useGetStatuses } from "../../../../shared/services/catalogService";
import {
  useCreateBuilding,
  useGetBuildingsCount,
  useUpdateBuilding,
} from "../../../buildings/services/buildingService";
import { useMemo, useState } from "react";
import type { Building } from "../../../../api/client/types/building.types";
import { toast } from "react-hot-toast";
import { TRANSLATIONS } from "../../../../shared/utils/translations";
import { useGetCities } from "../../../buildings/services/cityService";

export const useBuildingsTab = () => {
  const queryClient = useQueryClient();
  const buildingsResource = useBuildingsResource();
  const { data: statuses } = useGetStatuses();
  const { data: cities } = useGetCities();
  const { mutate: updateBuilding, isPending: isUpdating } = useUpdateBuilding();
  const { mutate: createBuilding, isPending: isCreating } = useCreateBuilding();
  
  // Get count of ACTIVE buildings only
  const { data: activeBuildingsCount } = useGetBuildingsCount({ status: "ACTIVE" });

  const [selectedBuilding, setSelectedBuilding] = useState<Building | null>(
    null
  );
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Editing logic
  const handleEditBuilding = (building: Building) =>
    setSelectedBuilding(building);
  const handleCloseEditModal = () => setSelectedBuilding(null);

  // Create building modal logic
  const handleOpenCreateModal = () => setIsCreateModalOpen(true);
  const handleCloseCreateModal = () => setIsCreateModalOpen(false);

  // Update building
  const handleUpdateBuilding = (data: {
    name: string;
    city_id: string;
    status_id: string;
  }) => {
    if (!selectedBuilding) return;

    updateBuilding(
      {
        id: selectedBuilding.id,
        data: {
          name: data.name,
          city_id: parseInt(data.city_id),
          status_id: parseInt(data.status_id),
        },
      },
      {
        onSuccess: () => {
          toast.success("Unidad residencial actualizada correctamente");
          handleCloseEditModal();
          buildingsResource.refetch();
          // Invalidate buildings count queries to refetch the active buildings count
          queryClient.invalidateQueries({ queryKey: ["buildings", "count"] });
        },
      }
    );
  };

  // Create building
  const handleCreateBuilding = (data: { name: string; city_id: string }) => {
    createBuilding(
      {
        name: data.name,
        city_id: parseInt(data.city_id),
      },
      {
        onSuccess: () => {
          toast.success("Unidad residencial creada correctamente");
          handleCloseCreateModal();
          buildingsResource.resetFilters();
          buildingsResource.handlePageChange(1);
          // Invalidate buildings list and count queries — React Query will refetch fresh data
          queryClient.invalidateQueries({ queryKey: ["buildings", "list"] });
          queryClient.invalidateQueries({ queryKey: ["buildings", "count"] });
        },
      }
    );
  };
  const defaultValues = useMemo(() => {
    return {
      name: selectedBuilding?.name ?? "",
      city_id: selectedBuilding?.city?.id
        ? String(selectedBuilding.city.id)
        : "",
      status_id: selectedBuilding?.status?.id
        ? String(selectedBuilding.status.id)
        : "",
    };
  }, [selectedBuilding]);
  const statusOptions = useMemo(() => {
    return (
      statuses?.map((status) => ({
        value: String(status.id),
        label:
          TRANSLATIONS.status[status.name as keyof typeof TRANSLATIONS.status],
      })) ?? []
    );
  }, [statuses]);
  const cityOptions = useMemo(() => {
    return (
      cities?.map((city) => ({
        value: String(city.id),
        label: city.name,
      })) ?? []
    );
  }, [cities]);
  return {
    buildingsResource,
    statusOptions,
    selectedBuilding,
    cityOptions,
    defaultValues,
    activeBuildingsCount: activeBuildingsCount?.count,
    
    // states
    isCreateModalOpen,
    isCreating,
    isUpdating,

    // handlers
    handleEditBuilding,
    handleCloseEditModal,
    handleOpenCreateModal,
    handleCloseCreateModal,
    handleUpdateBuilding,
    handleCreateBuilding,
  };
};
